import { VERSION_CHECK_CONFIG } from '@/config/constants';

const buildVersion: string = import.meta.env.VITE_APP_VERSION;

let polling = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let mismatchDetected = false;

function isRealVersion(v: string): boolean {
  return !!v && v !== 'dev';
}

function reloadForVersionMismatch(): void {
  mismatchDetected = true;
  stopPolling();
  window.location.reload();
}

async function checkVersion(): Promise<void> {
  if (!isRealVersion(buildVersion)) return;

  let resp: Response;
  try {
    resp = await fetch(VERSION_CHECK_CONFIG.VERSION_ENDPOINT, {
      cache: 'no-store',
    });
  } catch {
    return;
  }
  if (!resp.ok) return;

  let data: unknown;
  try {
    data = await resp.json();
  } catch {
    console.warn('[version-check] /version.json returned non-JSON response');
    return;
  }

  if (
    typeof data === 'object' &&
    data !== null &&
    'version' in data &&
    typeof (data as { version: unknown }).version === 'string'
  ) {
    const serverVersion = (data as { version: string }).version;
    if (isRealVersion(serverVersion) && serverVersion !== buildVersion) {
      reloadForVersionMismatch();
    }
  }
}

export function checkWebSocketVersion(serverVersion: string): void {
  if (!isRealVersion(buildVersion) || !isRealVersion(serverVersion)) return;
  if (serverVersion !== buildVersion) {
    reloadForVersionMismatch();
  }
}

function startPolling(): void {
  if (polling) return;
  polling = true;

  checkVersion();
  pollTimer = setInterval(checkVersion, VERSION_CHECK_CONFIG.POLL_INTERVAL);

  document.addEventListener('visibilitychange', onVisibilityChange);
}

function stopPolling(): void {
  if (!polling) return;
  polling = false;

  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  document.removeEventListener('visibilitychange', onVisibilityChange);
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') {
    checkVersion();
  }
}

export function useVersionCheck() {
  if (!import.meta.env.DEV && !polling && !mismatchDetected) {
    startPolling();
  }
}

export const _testApi = {
  checkVersion,
  checkWebSocketVersion,
  get polling() {
    return polling;
  },
  get mismatchDetected() {
    return mismatchDetected;
  },
  _reset() {
    polling = false;
    mismatchDetected = false;
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    document.removeEventListener('visibilitychange', onVisibilityChange);
  },
};
